import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { components } from 'react-select';
import { toast } from '../../../../../../ui/toast';
import { Button } from '../../../../../../ui';
import AsyncCustomSelect from '../../../../../../ui/custom_react_select/async_select';
import { CustomSelect } from '../../../../../../ui/custom_react_select/custom_select';
import { PencilIcon, TrashIcon } from '../../../../../../ui/icons/icons';
import { TextInput } from '../../../../../../ui/input/text_input/text_input';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import { searchPreScreenQuestions } from '../../../../../../utils/api_service/pre_screen_api';
import { debounce } from '../../../../../../utils/helpers/debounce';
import {
  Assessment,
  PreScreenQuestionBank,
} from '../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
import { usePreQualifyingQuestions } from './Hooks/UsePreQualifyingQuestions';
import { RootState } from '../../../../../../utils/redux_store/store';

interface IPreQualifyingQuestions {
  setActiveTab: (value: ((prevState: string) => string) | string) => void;
  assessmentInfo: Assessment | undefined;
  setAssessmentInfo: Dispatch<SetStateAction<Assessment | undefined>>;
  hasCCALicense?: boolean;
}

const PreQualifyingQuestions = (props: IPreQualifyingQuestions) => {
  const { setActiveTab, assessmentInfo, setAssessmentInfo, hasCCALicense } = props;
  const { t } = useTranslation(NS.adminPages);

  const questionTypes: SelectOptions[] = [
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.typeNumber), value: 'number' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.typeMultipleChoice), value: 'multiple-choice' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.typeLocation), value: 'location' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.typeText), value: 'text' },
  ];
  const { id } = useParams<{ id: string }>();
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  // const hasCCALicense = !!subscriptions?.hierTalentCca && Number(subscriptions.hierTalentCca.endDate) * 1000 > Date.now();
  const {
    choices,
    editChoices,
    setNewQuestionType,
    handleSaveQuestion,
    handleEditQuestion,
    handleDeleteQuestion,
    handleChoiceChange,
    handleAddQuestion,
    handleAddFromBank,
    setNewQuestionText,
    setEditChoices,
    editQuestionType,
    setEditQuestionType,
    editQuestionText,
    setEditQuestionText,
    editIndex,
    questions,
    newQuestionText,
    newQuestionType,
    setChoices,
    setEditIndex,
    isLoading,
  } = usePreQualifyingQuestions(id || '');
  const [defaultOptions, setDefaultOptions] = useState<SelectOptions[]>([]);
  const [searchQuestions, setSearchQuestions] = useState<
    PreScreenQuestionBank[]
  >([]);

  const getQuestionTypeLabel = (value: string): string => {
    const found = questionTypes.find((type) => type.value === value);
    return found ? found.label : value;
  };
  const createLabel = (question: string, questionType: string) => {
    return (
      <div className='flex items-center'>
        <div className='w-10/12'>
          <span>{ question }</span>
        </div>
        <div className='w-1/12 text-left'>
          <span className='text-gray-500 text-sm'>
            { getQuestionTypeLabel(questionType) }
          </span>
        </div>
      </div>
    );
  };

  const getNextTab = () => (hasCCALicense ? 'cca' : 'preview');
  const fetchOptions = async (inputValue: string): Promise<SelectOptions[]> => {
    try {
      const response = await searchPreScreenQuestions(inputValue, null, 1, 20);
      if (response.ok) {
        const data = await response.json();
        const questions: PreScreenQuestionBank[] = data.questions || [];
        setSearchQuestions(questions);
        return questions.map((question: PreScreenQuestionBank) => ({
          value: question.id || '',
          label: question.question,
          typeLabel:
            question.questionType === 'numeric'
              ? 'Numeric'
              : question.questionType.replace('-', ' '),
          raw: question,
        }));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
        return [];
      }
    } catch (error) {
      toast.error(`Error fetching questions: ${error}`, {
        position: 'bottom-center',
      });
      return [];
    }
  };
  const loadOptionsDebounced = debounce(
    async (
      inputValue: string,
      callback: (options: SelectOptions[]) => void,
    ) => {
      const options = await fetchOptions(inputValue);
      callback(options);
    },
    1000,
  );

  const handleQuestionChange = (selectedOption: SelectOptions) => {
    const bankQuestion: PreScreenQuestionBank = selectedOption.raw;
    if (bankQuestion) {
      // Check for duplicate in the main questions list
      const isDuplicate = questions.some(
        (q) => q.question === bankQuestion.question,
      );
      if (isDuplicate) {
        toast.error(t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.duplicateQuestion), {
          position: 'bottom-center',
        });
        return;
      }

      handleAddFromBank({
        question: bankQuestion.question,
        questionType: bankQuestion.questionType,
        choices: bankQuestion.choices || [],
      });
    }
  };
  const CustomOption = (props: any) => {
    return (
      <components.Option {...props}>
        <div className='flex items-center justify-between w-full'>
          <span className='text-[12px] font-semibold text-gray-700 truncate mr-4'>
            { props.data.label }
          </span>
          <span className='text-[10px] font-black text-indigo-400 bg-indigo-50 px-1.5 py-0.5 rounded uppercase tracking-tighter shrink-0'>
            { props.data.typeLabel }
          </span>
        </div>
      </components.Option>
    );
  };

  return (
    <div className='flex flex-col p-5 relative gap-6 bg-white rounded-xl shadow-sm border border-slate-200/60'>
      { /* ─── Header Section ─── */ }
      <div className='flex items-center justify-between mb-2 pb-2 border-b border-gray-100'>
        <div>
          <h1 className='text-sm font-light text-gray-800 tracking-wide'>
            { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.heading) }
          </h1>
          <p className='text-xs text-slate-500 mt-1'>
            { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.subtitle) }
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <span className='px-3 py-1 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full uppercase tracking-wider border border-amber-100'>
            { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.previewMode) }
          </span>
        </div>
      </div>

      { /* ─── Import from Bank Section ─── */ }
      <div className='flex flex-col gap-4 px-4 py-4 bg-indigo-50/20 rounded-xl border border-indigo-100/50'>
        <div className='lg:col-span-5'>
          <AsyncCustomSelect
              name='preScreenQuestion'
              value={[]}
              placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.searchPlaceholder)}
              defaultOptions={true}
              onChange={handleQuestionChange}
              loadOptions={(
                inputValue: string,
                callback: (options: SelectOptions[]) => void,
              ) => loadOptionsDebounced(inputValue, callback)}
              label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.importFromBank)}
              components={{ Option: CustomOption }}
          />
        </div>
      </div>

      { /* ─── Entry Section ─── */ }
      <div className='flex flex-col gap-5 px-4 py-4 bg-slate-50/40 rounded-xl border border-slate-100'>
        <div className='flex items-center gap-2 mb-1'>
          <div className='w-1 h-4 bg-emerald-400 rounded-full' />
          <h3 className='text-[11px] font-bold text-slate-800 uppercase tracking-wider'>
            { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.createNew) }
          </h3>
        </div>
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-4 items-end'>
          <div className='lg:col-span-7'>
            <TextInput
                label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.questionText)}
                name='question'
                value={newQuestionText}
                type='text'
                placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.questionPlaceholder)}
                onChange={(e) => setNewQuestionText(e.target.value)}
                disabled={editIndex !== null}
            />
          </div>
          <div className='lg:col-span-3'>
            <CustomSelect
                label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.responseType)}
                name='questionType'
                value={questionTypes.find(
                  (option) => option.value === newQuestionType,
                )}
                options={questionTypes}
                onChange={(selectedOption: SelectOptions) =>
                  setNewQuestionType(
                  selectedOption.value as
                    | 'number'
                    | 'multiple-choice'
                    | 'location'
                    | 'video-answer',
                  )
                }
                disabled={editIndex !== null}
            />
          </div>
          <div className='lg:col-span-2'>
            <Button
                onClick={() => handleAddQuestion()}
                variant='primary'
                label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.saveQuestion)}
                disabled={newQuestionText.trim() === '' || editIndex !== null}
                className='px-8'
            />
          </div>
        </div>

        { /* Choices Section for New Question */ }
        <div className='flex flex-col gap-4'>
          { newQuestionType === 'multiple-choice' && (
            <div className='flex flex-col gap-3 p-4 bg-white rounded-lg border border-slate-100'>
              <label className='text-[11px] font-semibold text-slate-500 uppercase tracking-wider'>
                { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.optionsChoices) }
              </label>
              <div className='flex flex-wrap gap-2'>
                { choices.map((choice, index) => (
                  <div key={index} className='flex items-center gap-2'>
                    <TextInput
                        name='choice'
                        value={choice}
                        type='text'
                        placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.choicePlaceholder, { index: index + 1 })}
                        className='w-48 !h-9'
                        onChange={(e) =>
                          handleChoiceChange(index, e.target.value, false)
                        }
                    />
                    { choices.length > 1 && (
                      <button
                          onClick={() =>
                            setChoices(choices.filter((_, i) => i !== index))
                          }
                          className='p-1.5 text-slate-300 hover:text-red-500 transition-colors'
                      >
                        <TrashIcon className='w-4 h-4' />
                      </button>
                    ) }
                  </div>
                )) }
                <Button
                    onClick={() => setChoices([...choices, ''])}
                    variant='secondary'
                    label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.addChoice)}
                    className='h-9 !py-0 border-dashed border-2'
                />
              </div>
            </div>
          ) }
        </div>
      </div>

      { /* ─── Questions List Section ─── */ }
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between px-1'>
          <h3 className='text-[11px] font-bold text-slate-600 uppercase tracking-wider'>
            { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.questionList) }
          </h3>
          <span className='text-[11px] font-medium text-slate-400'>
            { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.questionsDefined, { count: questions.length }) }
          </span>
        </div>

        { isLoading ? (
          <div className='flex justify-center p-10'>
            <span className='text-slate-400 italic'>{ t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.loading) }</span>
          </div>
        ) : (
          questions.map((question, indexNum) => (
            <div
                key={question.questionKey}
                className={`group flex flex-col gap-4 bg-white p-4 rounded-xl border transition-all duration-300
                ${
                  question.questionKey === editIndex
                    ? 'border-indigo-400 ring-4 ring-indigo-500/5 shadow-lg shadow-indigo-500/5'
                    : 'border-slate-100 hover:border-slate-200 hover:shadow-sm'
                }`}
            >
              { question.questionKey === editIndex ? (
                <div className='flex flex-col gap-5'>
                  <div className='grid grid-cols-1 md:grid-cols-12 gap-4'>
                    <div className='md:col-span-8'>
                      <TextInput
                          label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.editQuestion)}
                          name='question'
                          value={editQuestionText}
                          type='text'
                          onChange={(e) => setEditQuestionText(e.target.value)}
                      />
                    </div>
                    <div className='md:col-span-4'>
                      <CustomSelect
                          label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.type)}
                          name='questionType'
                          value={questionTypes.find(
                            (option) => option.value === editQuestionType,
                          )}
                          options={questionTypes}
                          onChange={(selectedOption: SelectOptions) =>
                            setEditQuestionType(
                            selectedOption.value as
                              | 'number'
                              | 'multiple-choice'
                              | 'location'
                              | 'video-answer',
                            )
                          }
                      />
                    </div>
                  </div>

                  { editQuestionType === 'multiple-choice' && (
                    <div className='flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg'>
                      { editChoices.map((choice, index) => (
                        <div key={index} className='flex items-center gap-2'>
                          <TextInput
                              name='choice'
                              value={choice}
                              type='text'
                              className='w-40 !h-8'
                              onChange={(e) =>
                                handleChoiceChange(index, e.target.value, true)
                              }
                          />
                          <button
                              onClick={() =>
                                setEditChoices(
                                  editChoices.filter((_, i) => i !== index),
                                )
                              }
                              className='p-1 text-slate-300 hover:text-red-500'
                          >
                            <TrashIcon className='w-3.5 h-3.5' />
                          </button>
                        </div>
                      )) }
                      <Button
                          onClick={() => setEditChoices([...editChoices, ''])}
                          variant='secondary'
                          label='+'
                          className='h-8 !w-8 justify-center !p-0'
                      />
                    </div>
                  ) }

                  <div className='flex justify-end gap-2'>
                    <Button
                        onClick={() => setEditIndex(null)}
                        variant='secondary'
                        label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.cancel)}
                        className='h-9 !py-0'
                    />
                    <Button
                        onClick={() => handleSaveQuestion()}
                        variant='primary'
                        label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.update)}
                        className='h-9 !py-0'
                    />
                  </div>
                </div>
              ) : (
                <div className='flex items-center gap-4'>
                  <div className='flex flex-col items-center justify-center w-8 h-8 shrink-0 rounded-lg bg-slate-100 text-slate-500 font-bold text-xs'>
                    { indexNum + 1 }
                  </div>

                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <p className='text-sm font-semibold text-slate-700 truncate'>
                        { question.question }
                      </p>
                      <span className='px-2 py-0.5 rounded bg-slate-50 border border-slate-100 text-[9px] font-bold uppercase text-slate-400'>
                        { getQuestionTypeLabel(question.questionType) }
                      </span>
                    </div>
                    { question.choices && (
                      <div className='flex gap-2 mt-1 flex-wrap'>
                        { question.choices.map((c, i) => (
                          <span
                              key={i}
                              className='text-[10px] text-slate-400 bg-slate-50 px-2 rounded-full border border-slate-100'
                          >
                            { c }
                          </span>
                        )) }
                      </div>
                    ) }
                  </div>

                  <div className='flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                    <button
                        className='p-2 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors'
                        onClick={() => handleEditQuestion(question)}
                    >
                      <PencilIcon className='w-4 h-4' />
                    </button>
                    <button
                        className='p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors'
                        onClick={() =>
                          question.questionKey &&
                        handleDeleteQuestion(question.questionKey)
                        }
                    >
                      <TrashIcon className='w-4 h-4' />
                    </button>
                  </div>
                </div>
              ) }
            </div>
          ))
        ) }
      </div>

      { /* ─── Footer Section ─── */ }
      <div className='flex justify-between items-center mt-6 pt-6 border-t border-slate-100'>
        <button
            onClick={() => setActiveTab('videoMessageDetails')}
            className='text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors'
        >
          { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.backToVideo) }
        </button>
        <Button
            onClick={() => setActiveTab(getNextTab())}
            variant='primary'
            label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.next)}
            className='shadow-lg shadow-indigo-500/20 px-10'
        />
      </div>
    </div>
  );
};

export default PreQualifyingQuestions;
